import ExcelJS from "exceljs";

export async function exportAttendanceExcel({
    rows = [],
    session = {},
    t,
    language = "fr",
}) {
    const workbook = new ExcelJS.Workbook();

    workbook.creator = "QCM ENS";
    workbook.created = new Date();
    workbook.modified = new Date();

    const isArabic = language?.startsWith("ar");
    const isEnglish = language?.startsWith("en");

    // ============================================================
    // OUTILS
    // ============================================================

    const locale = isArabic
        ? "ar-TN"
        : isEnglish
            ? "en-US"
            : "fr-FR";

    const formatDate = (value) => {
        if (!value) {
            return "-";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return "-";
        }

        return date.toLocaleString(locale);
    };

    const translate = (key) => {
        const value = t(key);

        return value && value !== key
            ? value
            : "";
    };

    // ============================================================
    // NOM DE LA FEUILLE
    // ============================================================

    const worksheetName = String(
        translate(
            "pages.examSessions.attendanceSheet.excelSheetName"
        ) || "Attendance"
    )
        .replace(/[\\/*?:[\]]/g, "")
        .substring(0, 31);

    const worksheet = workbook.addWorksheet(
        worksheetName || "Attendance"
    );

    // ============================================================
    // CONFIGURATION FEUILLE
    // ============================================================

    worksheet.views = [
        {
            rightToLeft: isArabic,
            state: "frozen",
            ySplit: 5,
        },
    ];

    // ============================================================
    // COLONNES
    // ============================================================

    worksheet.columns = [
        {
            key: "room",
            width: 28,
        },
        {
            key: "matricule",
            width: 18,
        },
        {
            key: "name",
            width: 30,
        },
        {
            key: "ipAddress",
            width: 20,
        },
        {
            key: "tablet",
            width: 22,
        },
        {
            key: "startedAt",
            width: 22,
        },
        {
            key: "submittedAt",
            width: 22,
        },
        {
            key: "status",
            width: 18,
        },
    ];

    // ============================================================
    // TITRE
    // ============================================================

    worksheet.mergeCells("A1:H1");

    const titleCell = worksheet.getCell("A1");

    titleCell.value = translate(
        "pages.examSessions.attendanceSheet.title"
    );

    titleCell.font = {
        name: "Arial",
        size: 18,
        bold: true,
        color: {
            argb: "FFFFFF",
        },
    };

    titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
            argb: "1F4E78",
        },
    };

    titleCell.alignment = {
        horizontal: "center",
        vertical: "middle",
    };

    worksheet.getRow(1).height = 38;

    // ============================================================
    // INFORMATIONS SESSION
    // ============================================================

    worksheet.mergeCells("A2:H2");

    const sessionCell = worksheet.getCell("A2");

    /*
     * Le backend retourne maintenant :
     *
     * session.exam_title
     *
     * provenant de :
     *
     * exams.title as exam_title
     */

    const examTitle =
        session?.exam_title ||
        session?.exam?.title ||
        session?.exam_name ||
        "";

    sessionCell.value =
        `${translate(
            "pages.examSessions.attendanceSheet.session"
        )} : ${examTitle}`;

    sessionCell.font = {
        name: "Arial",
        size: 12,
        bold: true,
        color: {
            argb: "404040",
        },
    };

    sessionCell.alignment = {
        horizontal: isArabic ? "right" : "left",
        vertical: "middle",
    };

    worksheet.getRow(2).height = 25;

    // ============================================================
    // DATES SESSION
    // ============================================================

    worksheet.mergeCells("A3:D3");
    worksheet.mergeCells("E3:H3");

    const startLabel = translate(
        "pages.examSessions.attendanceSheet.start"
    );

    const endLabel = translate(
        "pages.examSessions.attendanceSheet.end"
    );

    worksheet.getCell("A3").value =
        `${startLabel} : ${formatDate(session?.start_at)}`;

    worksheet.getCell("E3").value =
        `${endLabel} : ${formatDate(session?.end_at)}`;

    ["A3", "E3"].forEach((cellName) => {
        const cell = worksheet.getCell(cellName);

        cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
            color: {
                argb: "666666",
            },
        };

        cell.alignment = {
            horizontal: isArabic ? "right" : "left",
            vertical: "middle",
        };
    });

    worksheet.getRow(3).height = 24;

    // ============================================================
    // HEADER TABLEAU
    // ============================================================

    const headerRow = worksheet.getRow(5);

    headerRow.values = [
        translate(
            "pages.examSessions.attendanceSheet.columns.room"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.matricule"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.name"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.ipAddress"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.tablet"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.start"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.end"
        ),

        translate(
            "pages.examSessions.attendanceSheet.columns.status"
        ),
    ];

    headerRow.height = 32;

    headerRow.eachCell((cell) => {
        cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
            color: {
                argb: "FFFFFF",
            },
        };

        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
                argb: "2F75B5",
            },
        };

        cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
        };

        cell.border = {
            top: {
                style: "thin",
                color: {
                    argb: "D9E2F3",
                },
            },
            bottom: {
                style: "thin",
                color: {
                    argb: "D9E2F3",
                },
            },
            left: {
                style: "thin",
                color: {
                    argb: "D9E2F3",
                },
            },
            right: {
                style: "thin",
                color: {
                    argb: "D9E2F3",
                },
            },
        };
    });

    // ============================================================
    // DONNÉES
    // ============================================================

    rows.forEach((item, index) => {
        const statusKey =
            item.status
                ? `pages.examSessions.attendanceSheet.status.${item.status}`
                : null;

        const translatedStatus =
            statusKey
                ? translate(statusKey)
                : "";

        const row = worksheet.addRow({
            room: item.room || "",

            matricule:
                item.matricule || "",

            name:
                item.name || "",

            ipAddress:
                item.ipAddress || "",

            tablet:
                item.tablet || "",

            startedAt:
                item.startedAt
                    ? new Date(item.startedAt)
                    : null,

            submittedAt:
                item.submittedAt
                    ? new Date(item.submittedAt)
                    : null,

            status:
                translatedStatus ||
                item.status ||
                "",
        });

        row.height = 25;

        row.eachCell((cell) => {
            cell.font = {
                name: "Arial",
                size: 10,
                color: {
                    argb: "333333",
                },
            };

            cell.alignment = {
                vertical: "middle",
                horizontal: isArabic
                    ? "right"
                    : "left",
                wrapText: true,
            };

            cell.border = {
                top: {
                    style: "thin",
                    color: {
                        argb: "E6E6E6",
                    },
                },
                bottom: {
                    style: "thin",
                    color: {
                        argb: "E6E6E6",
                    },
                },
                left: {
                    style: "thin",
                    color: {
                        argb: "E6E6E6",
                    },
                },
                right: {
                    style: "thin",
                    color: {
                        argb: "E6E6E6",
                    },
                },
            };
        });

        // ========================================================
        // LIGNES ALTERNÉES
        // ========================================================

        if (index % 2 === 1) {
            row.eachCell((cell) => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: {
                        argb: "F8FAFC",
                    },
                };
            });
        }

        // ========================================================
        // FORMAT DATES
        // ========================================================

        row.getCell(6).numFmt =
            "dd/mm/yyyy hh:mm";

        row.getCell(7).numFmt =
            "dd/mm/yyyy hh:mm";

        // ========================================================
        // STATUT
        // ========================================================

        const statusCell =
            row.getCell(8);

        statusCell.alignment = {
            horizontal: "center",
            vertical: "middle",
        };

        statusCell.font = {
            name: "Arial",
            size: 10,
            bold: true,
        };
    });

    // ============================================================
    // FILTRE
    // ============================================================

    worksheet.autoFilter = {
        from: "A5",
        to: "H5",
    };

    // ============================================================
    // IMPRESSION
    // ============================================================

    worksheet.pageSetup = {
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        paperSize: 9,
    };

    worksheet.pageSetup.margins = {
        left: 0.25,
        right: 0.25,
        top: 0.5,
        bottom: 0.5,
        header: 0.2,
        footer: 0.2,
    };

    // ============================================================
    // FOOTER
    // ============================================================

    worksheet.headerFooter = {
        oddFooter: {
            center: translate(
                "pages.examSessions.attendanceSheet.footer"
            ),
            right: "Page &P / &N",
        },
    };

    // ============================================================
    // NOM DU FICHIER
    // ============================================================

    const fileName = translate(
        "pages.examSessions.attendanceSheet.fileName"
    );

    // ============================================================
    // GÉNÉRATION EXCEL
    // ============================================================

    const buffer =
        await workbook.xlsx.writeBuffer();

    // ============================================================
    // TÉLÉCHARGEMENT
    // ============================================================

    const blob = new Blob(
        [buffer],
        {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
    );

    const url =
        window.URL.createObjectURL(blob);

    const link =
        document.createElement("a");

    link.href = url;

    link.download =
        `${fileName}_${session?.exam_title || "session"}.xlsx`;

    document.body.appendChild(link);

    link.click();

    link.remove();

    window.URL.revokeObjectURL(url);
}